import { useStore } from '../../../store';
import {
  formatDate,
  formatDateV2,
  formatDateToISOString,
} from '../../../helpers/datesAndTime.helper';
import { ENRICHMENT_TYPES, TRANSACTION_DETAILS_DATA, TRANSACTION_TYPES } from '../constants';
import { formatNumberToMoneyString } from '../../../helpers/numberFormatters.helper';
import {
  ITransaction, ITransactionFilters, ITransactionsParams, ITrxData,
} from '../../../types';
import { httpService } from '../../../services/http.service';
import achFee from '../../../assets/icons/svg/transaction-icons/ic-transaction-fee.svg';
import { LiliTransaction } from '../../../pages/TransactionsFacelift/types/lili-transaction';

const { MUST_ENRICH_TYPES, NON_LOCATION_ENRICH_TYPES } = ENRICHMENT_TYPES;

const comboType1 = ['swipe', 'swipeRefund', 'swipeCashback'];
const comboType3 = ['adjustment', 'achTransfer', 'payment', 'referralGift', 'campaignGift', 'debitCardDeposit', 'directDepositReturn', 'taxBucketAutoSave', 'taxBucketManualSave', 'emergencyBucketAutoSave', 'emergencyBucketManualSave'];
const comboType4 = ['fee', 'interest', 'checkDepositFee'];
const comboType5 = ['atmWithdrawl', 'storeWithdrawl', 'cashDeposit', 'checkDeposit', 'checkDepositRejected', 'checkDepositBounced'];
const comboType6 = ['proFee'];
const comboType7 = ['billPay'];

const comboOne = {
  TimeAndDate: true,
  TransactionNameAndAmount: true,
  TransactionTag: true,
  BusinessPictureMap: true,
  BusinessDetailsRow: {
    address: true,
    website: true,
    phoneNo: true,
  },
  TextArea: true,
  TransactionReceipt: true,
};
const comboTwo = {
  TimeAndDate: true,
  TransactionNameAndAmount: true,
  NeedToVerify: true,
  NeedToVerifyDisclaimer: true,
};
const comboThree = {
  TimeAndDate: true,
  TransactionNameAndAmount: true,
  accountNumber: true,
  routingNumber: true,
  TextArea: true,
};
const comboFour = {
  TimeAndDate: true,
  TransactionNameAndAmount: true,
  TransactionTag: true,
};
const comboFive = {
  TimeAndDate: true,
  TransactionNameAndAmount: true,
  CheckPhoto: true,
  CheckNumber: true,
  TextArea: true,
  EstimatedArrivalDate: true,
};
const comboSix = {
  TimeAndDate: true,
  TransactionTag: true,
  TransactionNameAndAmount: true,
  TransactionEffectiveDate: true,
  HeaderIcon: achFee,
};
const comboSeven = {
  TimeAndDate: true,
  TransactionTag: true,
  TransactionNameAndAmount: true,
  TextArea: true,
  TransactionReceipt: true,
  mailCheckDetails: true,
};

const CHECK_STATUS_LIST = {
  SEND: 'SEND',
  ACCEPT: 'ACCEPT',
  APPROVED: 'APPROVED',
  REJECT: 'REJECT',
  SUCCESS: 'SUCCESS',
  BOUNCED: 'BOUNCED',
};

export const convertFilterValueToChipString = (value: keyof ITransactionFilters, key: string) => {
  switch (key) {
    case 'date': {
      // @ts-ignore
      return `${formatDate(new Date(value.min))} - ${formatDate(new Date(value.max))}`;
    }
    case 'type': {
      // @ts-ignore
      return value.key;
    }
    case 'amount': {
      // @ts-ignore
      return `${formatNumberToMoneyString(+value.min)} - ${formatNumberToMoneyString(+value.max)}`;
    }
    default: {
      return value;
    }
  }
};

export const getDefaultFilterDate = () => {
  let signUpDate = new Date();
  const summaryData = useStore.getState().getSummaryData();
  if (summaryData.customer) {
    const { customer: { createTime } } = summaryData;
    signUpDate = createTime;
  }
  const max = new Date();
  const min = new Date(signUpDate);
  return { max, min };
};

export const getInitialTransactionsFilters = () => {
  const { max, min } = getDefaultFilterDate();
  return {
    page: 1,
    type: {
      key: 'All Types',
      value: undefined,
    },
    recordCnt: TRANSACTION_DETAILS_DATA.MAX_RECORD_COUNT,
    date: {
      max,
      min,
    },
  } as ITransactionFilters;
};

export const convertFilterToQueryString = (filter: ITransactionFilters) => {
  const { max: maxEndDate, min: minStartDate } = getDefaultFilterDate();
  const params: ITransactionsParams = {
    accountNo: undefined,
    startDate: formatDateToISOString(filter.date?.min) || formatDateToISOString(minStartDate),
    endDate: formatDateToISOString(filter.date?.max) || formatDateToISOString(maxEndDate),
    recordCnt: TRANSACTION_DETAILS_DATA.MAX_RECORD_COUNT,
    page: filter.page || 1,
    pending: undefined,
    amountFrom: filter.amount?.min || undefined,
    amountTo: filter.amount?.max || undefined,
    transactionFilterType: filter.type?.value || undefined,
  };

  Object.entries(params).forEach(([key]) => {
    if (typeof params[key as keyof ITransactionsParams] === 'undefined') {
      delete params[key as keyof ITransactionsParams];
    }
  });
  return new URLSearchParams(params as {}).toString();
};

interface IGetTransactionType {
  activityTypeCode: string, trxType: string, trxTypeParams: string, type: string,
}

const getTransactionType = ({
  activityTypeCode, trxType, trxTypeParams, type,
}: IGetTransactionType) => {
  if (!activityTypeCode) return '';
  if (trxType) {
    switch (trxType) {
      case TRANSACTION_TYPES.REFERRAL_GIFT:
        return 'referralGift';

      case TRANSACTION_TYPES.BILL_PAY:
        return 'billPay';

      case TRANSACTION_TYPES.CAMPAIGN_GIFT:
        return 'campaignGift';

      case TRANSACTION_TYPES.TAX_JAR:
        if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.TAX_JAR_MANUAL) {
          return 'taxBucketManualSave';
        } if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.TAX_JAR_AUTO) {
          return 'taxBucketAutoSave';
        }
        break;

      case TRANSACTION_TYPES.CHECK_DEPOSIT:
        if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.CHECK_DEPOSIT_REJECTED) {
          return 'checkDepositRejected';
        } if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.CHECK_DEPOSIT_RETURN_ADJ) {
          return 'checkDepositBounced';
        }
        return 'checkDeposit';

      case TRANSACTION_TYPES.DEBIT_CARD_DEPOSIT:
        return 'debitCardDeposit';

      case TRANSACTION_TYPES.FEE:
        if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.CHECK_FEE) {
          return 'checkDepositFee';
        } if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.PRO_FEE) {
          return 'proFee';
        }
        return 'fee';

      case TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.ACH:
        if (trxTypeParams === TRANSACTION_TYPES.DIRECT_DEPOSIT) {
          return 'ach_transfer';
        }
        return 'achTransfer';

      case TRANSACTION_TYPES.EMERGENCY_BUCKET:
        if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.EMERGENCY_BUCKET_MANUAL) {
          return 'emergencyBucketManualSave';
        } if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.EMERGENCY_BUCKET_AUTO) {
          return 'emergencyBucketAutoSave';
        }
        break;

      case TRANSACTION_TYPES.CASH_BACK:
        return 'swipeCashback';

      case TRANSACTION_TYPES.INTEREST:
        if (trxTypeParams === TRANSACTION_TYPES.EXTERNAL_TYPE.SAVING_INTEREST) {
          return 'emergencySavingsAccountInterest';
        }
        return 'interest';

      case TRANSACTION_TYPES.DIRECT_DEPOSIT_RETURN:
        return 'directDepositReturn';

      case TRANSACTION_TYPES.CASH_DEPOSIT:
        return 'cashDeposit';

      default:
        break;
    }
  }

  if (type === 'W') {
    if (activityTypeCode === 'ST') {
      return 'storeWithdrawl';
    }
    return 'atmWithdrawl';
  }

  switch (activityTypeCode) {
    case 'AD':
      if (type === 'FM' || type === 'S') {
        return 'achTransfer';
      }
      return 'adjustment';

    case 'VI':
    case 'VS':
    case 'IS':
    case 'SD':
    case 'DB':
      if (type === 'Z') {
        return 'swipeRefund';
      } if (type === 'J') {
        return 'swipeCashback';
      }
      return 'swipe';

    case 'AT':
      return 'achTransfer';

    case 'FE':
      return 'fee';

    case 'PM':
    case 'PE':
      return 'payment';
    case 'MP':
      return 'atmWithdrawl';

    default:
      return '';
  }
};

const getComponentsMap = (type: string, onHold: boolean) => {
  if (comboType1.includes(type)) {
    return comboOne;
  } if (type === 'ach_transfer' && onHold) {
    return comboTwo;
  } if (comboType3.includes(type)) {
    return comboThree;
  } if (comboType4.includes(type)) {
    return comboFour;
  } if (comboType5.includes(type)) {
    return comboFive;
  } if (comboType6.includes(type)) {
    return comboSix;
  } if (comboType7.includes(type)) {
    return comboSeven;
  }
  return null;
};

export const getDetailsComponentsToShow = (transactionData: any) => {
  const {
    activityTypeCode, trxType, trxTypeParams, type, onHold,
  } = transactionData;
  const transactionType = getTransactionType({
    activityTypeCode, trxType, trxTypeParams, type,
  });
  return getComponentsMap(transactionType, onHold);
};

export const shouldEnrichTransaction = (transactionData: ITransaction | LiliTransaction) => {
  const externalType = transactionData?.externalTransactionType;
  if (transactionData?.found) return false;
  if (externalType && MUST_ENRICH_TYPES.includes(externalType)) return true;
  if (externalType && NON_LOCATION_ENRICH_TYPES.includes(externalType)) {
    return (transactionData?.locationDetails && !Object.keys(transactionData.locationDetails).length) || !('locationDetails' in transactionData);
  }
  return false;
};

interface IGetExtendedTransaction {
  activeTransaction: ITransaction | LiliTransaction;
  customerId: string;
  accountNo: string;
}

interface IGetExtendedTransactionV2 {
  activeTransaction: LiliTransaction;
  customerId: string;
  accountNo: string;
}

interface IProps {
  customerId: string,
  accountNo: string,
  transaction: ITransaction | LiliTransaction}

interface IQueryProps {
  sourceId: string | undefined,
  endDate: string | undefined,
  startDate: string | undefined,
  location?: string | undefined,
  category?: boolean | undefined,
}

const buildQueryParams = ({
  sourceId,
  endDate,
  startDate,
  location,
  category,
}: IQueryProps) => {
  const params = {
    sourceId: sourceId || undefined,
    endDate: endDate || undefined,
    startDate: startDate || undefined,
    location: location || undefined,
    category: category || undefined,
  };

  Object.entries(params)
    .forEach(([key]) => {
      if (typeof params[key as keyof IQueryProps] === 'undefined') {
        delete params[key as keyof IQueryProps];
      }
    });
  return new URLSearchParams(params as {}).toString();
};
export const getTransactionEnrichmentData = async ({
  customerId,
  accountNo,
  transaction,
}: IProps) => {
  const {
    activityTypeCode,
    transactionId,
    sourceId,
    locationKey,
    categoryDetails,
    timestamp,
  } = transaction;
  const endDate = new Date(new Date().setDate(new Date(timestamp).getDate() + 2)).toISOString();
  const startDate = new Date(new Date().setDate(new Date(timestamp).getDate() - 2)).toISOString();
  const isCategorized = !!categoryDetails?.categoryExternalId;
  const q = buildQueryParams({
    endDate,
    startDate,
    sourceId,
    location: locationKey,
    category: isCategorized,
  });
  const URL = `/api/v2/user/${customerId}/account/${accountNo}/${activityTypeCode}/${transactionId}?${q}`;
  try {
    const res = await httpService({
      method: 'get',
      url: URL,
    });
    return res.data;
  } catch (e) {
    return null;
  }
};
export const getExtendedTransaction = async ({ activeTransaction, customerId, accountNo }: IGetExtendedTransaction) => {
  if (shouldEnrichTransaction(activeTransaction)) {
    try {
      const enrichedData = await getTransactionEnrichmentData({
        transaction: activeTransaction,
        customerId,
        accountNo,
      });
      if (enrichedData && typeof enrichedData === 'object') {
        return { ...enrichedData, ...activeTransaction };
      }
      return activeTransaction;
    } catch (e) {
      console.error(e);
      return activeTransaction;
    }
  } else {
    return activeTransaction;
  }
};

export const getExtendedTransactionV2 = async ({ activeTransaction, customerId, accountNo }: IGetExtendedTransactionV2) => {
  if (shouldEnrichTransaction(activeTransaction)) {
    try {
      const enrichedData = await getTransactionEnrichmentData({
        transaction: activeTransaction,
        customerId,
        accountNo,
      });
      if (enrichedData && typeof enrichedData === 'object') {
        Object.assign(activeTransaction, enrichedData);
        return activeTransaction;
      }
      return activeTransaction;
    } catch (e) {
      console.error(e);
      return activeTransaction;
    }
  } else {
    return activeTransaction;
  }
};

const getModifiedStatus = (checkDetailsData: any, transactionStatus: any, translator:any) => {
  if (checkDetailsData) {
    if (transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING) {
      switch (checkDetailsData?.status) {
        case CHECK_STATUS_LIST.SEND:
        case CHECK_STATUS_LIST.ACCEPT:
          return translator('TRANSACTION_STATUS.IN_REVIEW');
        case CHECK_STATUS_LIST.APPROVED:
          return translator('TRANSACTION_STATUS.IN_PROCESS');
        default:
          return `ETA: ${formatDateV2(checkDetailsData?.estimatedArrivalDate, true)}`;
      }
    }
    return '';
  }
  return '';
};

export const getTransactionStatus = ({
  trxType, externalTransactionType, trxTypeParams, transactionStatus, checkDetailsData,
}: ITrxData, translator?: any) => {
  const modifiedStatus = getModifiedStatus(checkDetailsData, transactionStatus, translator);
  if (modifiedStatus) return modifiedStatus;
  // checks
  if (trxType === TRANSACTION_TYPES.CHECK_DEPOSIT) {
    if (externalTransactionType === TRANSACTION_TYPES.EXTERNAL_TYPE.CHECK_DEPOSIT_REJECTED) {
      return TRANSACTION_TYPES.CHECK_STATUS.REJECTED;
    }
    if (externalTransactionType === TRANSACTION_TYPES.EXTERNAL_TYPE.CHECK_DEPOSIT_BOUNCED) {
      return TRANSACTION_TYPES.CHECK_STATUS.RETURNED;
    }
    if (transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING && checkDetailsData) {
      if (checkDetailsData.status) {
        if (checkDetailsData.status === TRANSACTION_TYPES.CHECK_STATUS.SEND || checkDetailsData.status === TRANSACTION_TYPES.CHECK_STATUS.ACCEPT) {
          return TRANSACTION_TYPES.CHECK_STATUS.IN_REVIEW;
        } if (checkDetailsData.status === TRANSACTION_TYPES.CHECK_STATUS.APPROVED) {
          return TRANSACTION_TYPES.CHECK_STATUS.IN_PROCESS;
        }
        return TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING;
      }
    }
  }
  // direct deposits
  if (trxType === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.ACH && (externalTransactionType === TRANSACTION_TYPES.DIRECT_DEPOSIT || trxTypeParams === TRANSACTION_TYPES.DIRECT_DEPOSIT)) {
    if (transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING) return TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING;
    if (transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.HOLD) return TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.ON_HOLD;
    return TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.SETTLED;
  }
  // any other transaction type
  if (transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING) {
    return TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING;
  }
  return TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.SETTLED;
};

export const getNameFromTransaction = (transaction: ITransaction) => transaction.trxTitle || transaction.title || transaction.merchantName || transaction.billerName || transaction.prettyName || transaction.details;
